import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, File, FileText, Image, Archive, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const getFileIcon = (fileType) => {
  const type = fileType?.toLowerCase();
  if (['.jpg', '.jpeg', '.png', '.gif', '.tiff', '.tif', '.bmp'].includes(type)) {
    return <Image className="w-5 h-5 text-blue-500" />;
  }
  if (['.pdf', '.ai', '.eps', '.psd', '.svg'].includes(type)) {
    return <FileText className="w-5 h-5 text-red-500" />;
  }
  if (['.zip', '.rar'].includes(type)) {
    return <Archive className="w-5 h-5 text-amber-500" />;
  }
  return <File className="w-5 h-5 text-ivory-500" />;
};

const ALLOWED_EXTENSIONS = [
  '.pdf',  // PDF documents
  '.ai',   // Adobe Illustrator
  '.eps',  // Encapsulated PostScript
  '.svg',  // Scalable Vector Graphics
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MAX_FILES = 10;

const ArtworkUpload = ({ 
  onFilesUploaded, 
  uploadedFiles = [], 
  sessionId,
  onSessionIdChange,
  disabled = false 
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type ${ext} not allowed`;
    }
    
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (max ${MAX_FILE_SIZE / (1024 * 1024)}MB)`;
    }
    
    return null;
  };

  const uploadFiles = async (files) => {
    if (files.length === 0) return;
    
    if (uploadedFiles.length + files.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    // Validate all files
    const validFiles = [];
    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        setError(`${file.name}: ${error}`);
        return;
      }
      validFiles.push(file);
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      validFiles.forEach(file => formData.append('files', file));
      if (sessionId) {
        formData.append('session_id', sessionId);
      }

      const response = await fetch(`${API_URL}/api/uploads/artwork`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      if (data.success) {
        // Update session ID if new
        if (data.session_id && !sessionId) {
          onSessionIdChange?.(data.session_id);
        }
        
        // Add uploaded files to list
        const newFiles = [...uploadedFiles, ...data.uploaded_files];
        onFilesUploaded(newFiles);
        
        // Show any partial errors
        if (data.errors?.length > 0) {
          setError(data.errors.map(e => `${e.filename}: ${e.error}`).join(', '));
        }
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled || uploading) return;
    
    const files = Array.from(e.dataTransfer.files);
    uploadFiles(files);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, uploading, uploadedFiles, sessionId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !uploading) {
      setDragActive(true);
    }
  }, [disabled, uploading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    uploadFiles(files);
    // Reset input
    e.target.value = '';
  };

  const removeFile = (fileId) => {
    const newFiles = uploadedFiles.filter(f => f.id !== fileId);
    onFilesUploaded(newFiles);
  };

  return (
    <div className="space-y-4" data-testid="artwork-upload">
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-6 text-center transition-all
          ${dragActive 
            ? 'border-gold-500 bg-gold-50' 
            : 'border-ivory-300 bg-ivory-50 hover:border-gold-400 hover:bg-ivory-100'
          }
          ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || uploading}
        />
        
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-10 h-10 text-gold-600 animate-spin" />
            <p className="text-ivory-600 font-body">Uploading artwork...</p>
          </div>
        ) : (
          <>
            <Upload className={`w-10 h-10 mx-auto mb-3 ${dragActive ? 'text-gold-600' : 'text-ivory-400'}`} />
            <p className="text-ivory-800 font-medium font-body mb-1">
              {dragActive ? 'Drop files here' : 'Upload Your Artwork'}
            </p>
            <p className="text-ivory-500 text-sm font-body mb-2">
              Drag & drop files or click to browse
            </p>
            <p className="text-ivory-400 text-xs font-body">
              Supported: PDF, AI, EPS, SVG (vector files only) • Max 50MB per file
            </p>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-600 text-sm font-body">{error}</p>
          <button 
            onClick={() => setError('')}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-ivory-700 font-body flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-600" />
            Uploaded Files ({uploadedFiles.length}/{MAX_FILES})
          </p>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div 
                key={file.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-ivory-100 border border-ivory-200"
              >
                {getFileIcon(file.file_type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ivory-800 font-body truncate">
                    {file.original_name}
                  </p>
                  <p className="text-xs text-ivory-500 font-body">
                    {file.file_size_display}
                  </p>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="p-1 rounded hover:bg-ivory-200 text-ivory-400 hover:text-red-500 transition-colors"
                  title="Remove file"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Helper Text */}
      <p className="text-xs text-ivory-400 font-body">
        Upload your artwork files for production. Our team will review and contact you for approval before production begins.
      </p>
    </div>
  );
};

export default ArtworkUpload;
